import { Box, Text } from '@fullstory/components';
import React from 'react';
import { ConversionOverlayFunnelDetails } from '../../ClickMaps';
import { FormattedMessage } from 'react-intl';
import { classNames, percentage, shortNumber } from '@fullstory/util/fmt';
import { intl } from '@fullstory/intl';
import css from './index.module.scss';

interface ConversionElementStatsProps {
  elementStats: ConversionOverlayFunnelDetails | undefined;
  /** Text value of the selected goal. */
  conversionEventLabel: string;
  color?: React.ComponentProps<typeof Text>['color'];
}

export const ConversionElementStatsDetails: React.FC<ConversionElementStatsProps> = ({
  elementStats,
  conversionEventLabel,
  color,
}) => {
  if (!elementStats) {
    return null;
  }

  const numUsersClicked = shortNumber(elementStats.numUsersClicked);
  const numUsersConverted = shortNumber(elementStats.numUsersConverted);
  const percentConverted = percentage(
    elementStats.numUsersClicked
      ? elementStats.numUsersConverted / elementStats.numUsersClicked
      : 0,
    true,
    {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
  );
  const boldAppearanceOverride = classNames({
    [css.inverseEmphasized]: color === 'default-inverse',
  });

  return (
    <>
      <Text appearance="bodySmall" color={color}>
        <FormattedMessage defaultMessage="Users clicked" id="WikcHi" />
      </Text>
      <Text
        appearance="bodySmallBold"
        color={color ?? 'emphasized'}
        className={boldAppearanceOverride}
      >
        {numUsersClicked}
      </Text>
      <Box padding={[16, 0]}>
        <Text appearance="bodySmall" color={color}>
          <FormattedMessage defaultMessage="Users converted" id="nJ5ClK" />
        </Text>
        <Text appearance="bodySmall" color={color}>
          <FormattedMessage
            defaultMessage="{numUsersConverted} users ∙ <f>{percentage}</f>"
            id="nWcjWm"
            values={{
              f(chunks) {
                return (
                  <Text
                    appearance="bodySmallBold"
                    className={boldAppearanceOverride}
                    color={color ?? 'emphasized'}
                    tag="span"
                  >
                    {chunks}
                  </Text>
                );
              },
              numUsersConverted,
              percentage: percentConverted,
            }}
          />
        </Text>
      </Box>
      <Text appearance="bodySmall" color={color}>
        <FormattedMessage
          defaultMessage="Of the <userText>{numUsersClicked}</userText> who clicked on this element, <b>{percentConverted}</b> converted to <b>{conversionEventLabel}</b> in the same session."
          id="02EhRw"
          values={{
            userText: chunks => (
              <Text
                appearance="bodySmallBold"
                className={boldAppearanceOverride}
                color={color ?? 'emphasized'}
                tag="span"
              >
                {chunks} {intl.formatMessage({ defaultMessage: 'users', id: 'V6wm5v' })}
              </Text>
            ),
            b: chunks => (
              <Text
                appearance="bodySmallBold"
                className={classNames(boldAppearanceOverride, css.conversionEventLabel)}
                color={color ?? 'emphasized'}
                tag="span"
              >
                {chunks}
              </Text>
            ),
            numUsersClicked,
            percentConverted,
            conversionEventLabel,
          }}
        />
      </Text>
    </>
  );
};
